<template>
  <div class="change-policy-no-dialog">
    <el-dialog
      title="修改保单号"
      :visible.sync="dialogVisible"
      width="600px"
      :before-close="handleClose">
      <el-form :model="formData" :rules="rules" ref="policyForm" label-width="120px" v-loading="loading">
        <el-form-item label="订单ID：">
          <span>{{ formData.id }}</span>
        </el-form-item>
        
        <el-form-item label="当前保单号：">
          <span>{{ formData.current_policy_no }}</span>
        </el-form-item>
        
        <el-form-item label="新保单号：" prop="new_policy_no">
          <el-input
            v-model="formData.new_policy_no"
            placeholder="请输入新的保单号">
          </el-input>
        </el-form-item>
        
        
        <el-form-item label="上传附件：">
          <el-upload
            ref="upload"
            :action="uploadUrl"
            :headers="uploadHeaders"
            :on-success="handleUploadSuccess"
            :on-error="handleUploadError"
            :before-upload="beforeUpload"
            :file-list="fileList"
            name="fileVal"
            :on-remove="handleRemove"
            multiple
            :limit="5">
            <el-button size="small" type="primary">点击上传</el-button>
            <div slot="tip" class="el-upload__tip">只能上传jpg/png/pdf文件，且不超过10MB</div>
          </el-upload>
        </el-form-item>
      </el-form>
      
      <span slot="footer" class="dialog-footer">
        <el-button @click="handleClose">取消</el-button>
        <el-button type="primary" @click="handleSubmit" :loading="submitLoading">确定</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { ygbOrderUpdatePolicyNo, ygbOrderInfo } from '@/api/member'

export default {
  name: 'ChangePolicyNo',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    rowData: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    // 自定义验证规则
    const validatePolicyNo = (rule, value, callback) => {
      if (value === '') {
        callback(new Error('请输入保单号'))
      } else if (value.length > 50) {
        callback(new Error('保单号长度不能超过50个字符'))
      } else {
        callback()
      }
    }
    
    const validateConfirmPolicyNo = (rule, value, callback) => {
      if (value === '') {
        callback(new Error('请再次输入保单号'))
      } else {
        callback()
      }
    }
    
    return {
      dialogVisible: this.visible,
      loading: false,
      submitLoading: false,
      formData: {
        id: '',
        current_policy_no: '',
        new_policy_no: ''
      },
      fileList: [],
      uploadedFiles: [],
      uploadUrl: process.env.VUE_APP_FILE_UPLOAD_URL || process.env.VUE_APP_BASE_URL + '/res/file/upload',
      uploadHeaders: {
        'AppKey': 'BpLnfgDs'
      },
      rules: {
        new_policy_no: [
          { required: true, validator: validatePolicyNo, trigger: 'blur' }
        ]
      }
    }
  },
  watch: {
    visible(newVal) {
      this.dialogVisible = newVal
      if (newVal) {
        this.initFormData()
      }
    }
  },
  methods: {
    async initFormData() {
      if (this.rowData && Object.keys(this.rowData).length > 0) {
        this.formData = {
          id: this.rowData.id,
          current_policy_no: this.rowData.policy_no || '',
          new_policy_no: ''
        }
        
        // 重置文件列表
        this.fileList = []
        this.uploadedFiles = []
        
        // 获取订单详情，回显已有附件
        await this.loadOrderInfo()
      }
    },
    async loadOrderInfo() {
      try {
        this.loading = true
        const response = await ygbOrderInfo({
          id: this.formData.id,
          platform_type: 8
        })
        
        if (response.err_code === 0 && response.order && response.order.policy_file) {
          // 回显已有附件
          const existingFiles = response.order.policy_file.map((item, index) => {
            return {
              name: item.filename,
              url: item.url,
              uid: Date.now() + index, // 生成唯一ID
              status: 'success'
            }
          })
          
          // 设置文件列表用于显示
          this.fileList = [...existingFiles]
          
          // 设置已上传文件列表用于提交
          this.uploadedFiles = existingFiles.map(file => ({
            filename: file.name,
            url: file.url
          }))
        }
      } catch (error) {
        console.error('获取订单详情失败:', error)
        this.$message.error('获取订单详情失败')
      } finally {
        this.loading = false
      }
    },
    handleClose() {
      this.dialogVisible = false
      this.$emit('update:visible', false)
      this.$refs.policyForm.resetFields()
      // 清空文件列表
      this.fileList = []
      this.uploadedFiles = []
    },
    async handleSubmit() {
      this.$refs.policyForm.validate(async (valid) => {
        if (valid) {
          this.submitLoading = true
          try {
            const response = await ygbOrderUpdatePolicyNo({
              id: this.formData.id,
              policy_no: this.formData.new_policy_no,
              policy_file: this.uploadedFiles
            })
            
            if (response.err_code === 0) {
              this.$message.success('保单号修改成功')
              this.$emit('success')
              this.handleClose()
            } else {
              this.$message.error(response.data?.err_msg || '修改保单号失败')
            }
          } catch (error) {
            console.error('修改保单号失败:', error)
            this.$message.error('修改保单号失败，请稍后重试')
          } finally {
            this.submitLoading = false
          }
        }
      })
    },
    beforeUpload(file) {
      const isValidType = ['image/jpeg', 'image/png', 'application/pdf'].includes(file.type)
      const isLt10M = file.size / 1024 / 1024 < 10
      
      if (!isValidType) {
        this.$message.error('上传文件只能是 JPG/PNG/PDF 格式!')
        return false
      }
      if (!isLt10M) {
        this.$message.error('上传文件大小不能超过 10MB!')
        return false
      }
      return true
    },
    handleUploadSuccess(response, file, fileList) {
      if (response.err_code === 0) {
        // 检查是否已存在相同文件，避免重复添加
        const existingFile = this.uploadedFiles.find(item => 
          item.filename === file.name && item.url === response.data
        )
        
        if (!existingFile) {
          this.uploadedFiles.push({
            filename: file.name,
            url: response.data
          })
        }
        this.$message.success('文件上传成功')
      } else {
        this.$message.error(response.err_msg || '文件上传失败')
        // 移除上传失败的文件
        this.fileList = this.fileList.filter(item => item.uid !== file.uid)
      }
    },
    handleUploadError(err, file, fileList) {
      this.$message.error('文件上传失败')
      console.error('上传错误:', err)
    },
    handleRemove(file, fileList) {
      // 从已上传文件列表中移除
      this.uploadedFiles = this.uploadedFiles.filter(item => {
        // 对于已有文件，通过filename和url匹配
        if (file.url && item.url) {
          return !(item.filename === file.name && item.url === file.url)
        }
        // 对于新上传文件，通过filename匹配
        return item.filename !== file.name
      })
    }
  }
}
</script>

<style lang="less" scoped>
.change-policy-no-dialog {
  /deep/ .el-dialog__body {
    padding: 20px;
  }
  
  .el-form-item {
    margin-bottom: 20px;
  }
  
  .el-upload__tip {
    color: #606266;
    font-size: 12px;
    margin-top: 7px;
  }
}
</style>